import * as Sentry from '@sentry/browser';
import { supportsProfilePhoto } from '@sr/templates/src';
import { Resume } from '@sr/types';
import { nanoid } from 'nanoid';
import React from 'react';
import { Box, Button, CameraIcon, Image, Input, Text } from '../../ui';
import API from '../../utils/api';
import { storage } from '../../utils/firebase';

const ProfilePhoto = ({ resume }: { resume: Resume }) => {
  const fileInput = React.createRef<HTMLInputElement>();
  const { profilePhotoUrl } = resume;
  const { activeTemplateId } = resume.style;

  function handleChange(e: React.FormEvent<HTMLInputElement>) {
    e.preventDefault();
    if (
      fileInput &&
      fileInput.current &&
      fileInput.current.files &&
      fileInput.current.files[0]
    ) {
      const file = fileInput.current.files[0];
      updateProfilePhotoUrl(file);
      void API.events.logEvent({
        userId: resume.owner,
        eventName: 'Resume Added Profile Photo',
      });
    }
  }

  function updateProfilePhotoUrl(file: File) {
    const fileExtension = file.name.split('.').pop();
    const storageRef = storage.ref(
      `users/${resume.owner}/files/${nanoid()}.${fileExtension}`
    );

    // Remove file if one already exists
    removeProfilePhotoFromStorage(resume);

    // Upload the file and add http url to the resume in the db
    storageRef.put(file).then((snapshot) => {
      snapshot.ref.getDownloadURL().then(function (downloadUrl) {
        API.resume.updateProfilePhotoUrl({
          resume,
          profilePhotoUrl: makeCloudinaryResizeUrl(downloadUrl),
          profilePhotoSrc: downloadUrl,
        });
      });
    });
  }

  // Create a Cloudinary fetch API URL that resizes the image to 1000px
  // This is needed because some people were uploading huge photos that were
  // crashing the PDF rendering. It also makes web resumes smaller.
  function makeCloudinaryResizeUrl(src: string) {
    return `https://assets.standardresume.co/image/fetch/w_300,h_300,c_fill,g_face,f_png/${encodeURIComponent(
      src
    )}`;
  }

  function deleteProfilePhoto() {
    if (resume.profilePhotoUrl) {
      removeProfilePhotoFromStorage(resume);
      void API.resume.updateProfilePhotoUrl({
        resume,
        profilePhotoUrl: null,
        profilePhotoSrc: null,
      });
    }
  }

  // I changed profilePhotoUrl to be the Cloudinary fetch URL instead of the
  // firebase storage photo here:
  // https://github.com/mnmmio/sr-app/commit/cbf67a8e71ff1a368a42ab0f85158813bf684353
  //
  // This broke deleting photos because this code assumed profilePhotoUrl was
  // the firebase storage URL.
  //
  // Now I'm adding profilePhotoSrc to store the firebase url for deleting, but
  // old resumes will still have the src in their profilePhotoUrl, so we need
  // to check if the Src exists and otherwise try to delete the profilePhotoUrl.
  function removeProfilePhotoFromStorage(resume: Resume) {
    // Don't try and delete the default photo that we add to new resumes
    if (
      resume.profilePhotoUrl &&
      resume.profilePhotoUrl.includes('default-profile-photo')
    ) {
      return;
    }

    try {
      if (resume.profilePhotoSrc) {
        const ref = storage.refFromURL(resume.profilePhotoSrc);
        ref.delete();
      } else if (resume.profilePhotoUrl) {
        const ref = storage.refFromURL(resume.profilePhotoUrl);
        ref.delete();
      } else {
        throw new Error(
          'Resume missing profilePhotoUrl and profilePhotoSrc properties'
        );
      }
    } catch (error) {
      Sentry.captureException(error, { tags: { noFeedback: true } });
    }
  }

  function openUpload() {
    fileInput.current && fileInput.current.click();
  }

  // Don't show the photo picker if the template doesn't support photos
  if (!supportsProfilePhoto(activeTemplateId)) return null;

  return (
    <Box
      sx={{
        display: 'flex',
        width: '100%',
        pr: 4,
        pt: 3,
        pb: 0,
      }}
    >
      {profilePhotoUrl ? (
        <Image
          src={profilePhotoUrl}
          sx={{
            width: 32,
            height: 32,
            borderRadius: 999,
            objectFit: 'cover',
            flexShrink: 0,
          }}
        />
      ) : (
        <Box
          onClick={openUpload}
          sx={{
            width: 32,
            height: 32,
            borderRadius: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            flexShrink: 0,
          }}
        >
          <CameraIcon color="white" />
        </Box>
      )}
      <Box sx={{ pl: 3, width: '100%' }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
          }}
        >
          <Text variant="baseEmphasis" pb={1} mt="6px" color="white">
            Profile Photo
          </Text>
        </Box>
        {profilePhotoUrl && (
          <Box sx={{ display: 'flex' }}>
            <Button
              onClick={openUpload}
              data-testid="change-photo"
              sx={{
                variant: 'buttons.link',
                pl: 0,
                pt: 3,
                fontSize: 'caption',
                color: 'white50',
              }}
            >
              Change
            </Button>
            <Button
              onClick={deleteProfilePhoto}
              data-testid="delete-photo"
              sx={{
                variant: 'buttons.link',
                pl: 0,
                pt: 3,
                fontSize: 'caption',
                color: 'white50',
              }}
            >
              Delete
            </Button>
          </Box>
        )}
        {!profilePhotoUrl && (
          <Button
            onClick={openUpload}
            data-testid="upload-photo"
            sx={{
              variant: 'buttons.link',
              pl: 0,
              pt: 3,
              fontSize: 'caption',
              color: 'blueDark',
            }}
          >
            Upload
          </Button>
        )}
        <form>
          <Input
            type="file"
            accept="image/png, image/jpeg"
            ref={fileInput}
            onChange={handleChange}
            sx={{ display: 'none' }}
          />
        </form>
      </Box>
    </Box>
  );
};

export default ProfilePhoto;
